import React, { useMemo } from 'react';
import { BLOCKS } from '@contentful/rich-text-types';
import ReactHtmlParser from 'react-html-parser';

import RichText from '../../UI/RichText';
import ButtonLink from '../../UI/ButtonLink/ButtonLink';
import TextLink from '../../UI/TextLink/TextLink';
import { PageSection } from '../../../ContentfulTypes';

import {
  Wrapper,
  Header,
  Headline,
  Container,
  Card,
  Icon,
  Title,
  Description,
  CtaContainer,
} from './styles';

interface Props {
  data: PageSection;
}

const headlineOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Headline>{children}</Headline>,
  },
};

const IconTextSection = ({ data }: Props) => {
  const heading = useMemo(() => data?.combinatorSectionItems?.find(
    (section) => section?.__typename === 'ContentfulTextSection',
  ), [data?.combinatorSectionItems]);

  const cta = useMemo(() => data?.combinatorSectionItems?.find(
    (section) => section?.__typename === 'ContentfulCta',
  ), [data?.combinatorSectionItems]);
  
  const cards = useMemo(() => data?.combinatorSectionItems?.filter(
    (section) => section?.__typename === 'ContentfulCardItem',
  ), [data?.combinatorSectionItems]);

  return (
    <Wrapper data-testid="icon-text-section">
      {(heading || cta) && (
        <Header>
          {heading && (
            <RichText document={heading?.heading} options={headlineOptions} />
          )}
          {cta && (
            <TextLink
              linkText={cta?.ctaLabel?.ctaLabel}
              linkColor={cta?.ctaColor}
              linkSize="small"
              ariaLabel={cta.ctaAriaLabel}
              route={cta?.ctaTarget}
              url={cta?.ctaUrl}
              className="text-link"
              data-testid="section-cta-text"
            />
          )}
        </Header>
      )}
      {cards && (
        <Container>
          {cards?.map((items) => (
            <Card key={items?.contentful_id}>
              {items?.cardIcon && (
                <Icon image={items?.cardIcon} />
              )}
              {items?.cardHeading && (
                <Title data-testid="card-title">
                  {ReactHtmlParser(items?.cardHeading)}
                </Title>
              )}
              {items?.cardDescription && (
                <Description data-testid="card-desc">
                  {ReactHtmlParser(items?.cardDescription?.cardDescription)}
                </Description>
              )}
              {items?.cardCta && (
                <CtaContainer>
                  {items?.cardCta?.ctaType === 'button' && (
                    <ButtonLink
                      buttonText={items?.cardCta?.ctaLabel?.ctaLabel}
                      buttonColor={items?.cardCta?.ctaColor}
                      buttonSize="small"
                      ariaLabel={items?.cardCta?.ctaAriaLabel}
                      route={items?.cardCta?.ctaTarget}
                      url={items?.cardCta?.ctaUrl}
                      data-testid="cta-button"
                    />
                  )}
                  {items?.cardCta?.ctaType === 'text-link' && (
                    <TextLink
                      linkText={items?.cardCta?.ctaLabel?.ctaLabel}
                      linkColor={items?.cardCta?.ctaColor}
                      linkSize="small"
                      ariaLabel={items?.cardCta?.ctaAriaLabel}
                      route={items?.cardCta?.ctaTarget}
                      url={items?.cardCta?.ctaUrl}
                      className="text-link"
                      data-testid="cta-text"
                    />
                  )}
                </CtaContainer>
              )}
            </Card>
          ))}
        </Container>
      )}
    </Wrapper>
  );
};

export default IconTextSection;
